import { Form, Select, Table, Checkbox } from 'antd'
import React, { Component, useContext, useEffect, useRef, useState } from 'react'
import { PricingAPI } from '../../../controller/PricingAPI';
import HotelSelect from '../../components/HotelSelect';
import moment from 'moment';
import RoomCategorySelect from '../../components/RoomCategorySelect';

const EditableContext = React.createContext(null);
const EditableRow = ({ index, ...props }) => {
    const [form] = Form.useForm();
    return (
        <Form form={form} component={false}>
            <EditableContext.Provider value={form}>
                <tr {...props} />
            </EditableContext.Provider>
        </Form>
    );
};
const EditableCell = ({
    title,
    editable,
    children,
    dataIndex,
    record,
    handleSave,
    ...restProps
}) => {
    const [editing, setEditing] = useState(false);
    const inputRef = useRef(null);
    const form = useContext(EditableContext);
    useEffect(() => {
        if (editing) {
            inputRef.current.focus();
        }
    }, [editing]);

    const toggleEdit = () => {
        setEditing(!editing);
        form.setFieldsValue({
            [dataIndex]: record[dataIndex],
        });
    };

    const save = async () => {
        try {
            const values = await form.validateFields();
            toggleEdit();
            handleSave({ ...record, ...values });
        } catch (errInfo) {
            console.log('Save failed:', errInfo);
        }
    };

    let childNode = children;

    if (editable) {
        childNode = editing ? (
            <Form.Item
                style={{ margin: 0, }}
                name={dataIndex}
            >
                <Select ref={inputRef} style={{ minWidth: 150 }} value={children} onChange={save}>
                    <Select.Option key={'none'} value={null}>無</Select.Option>
                    <Select.Option key={'double'} value={'double'}>一大床</Select.Option>
                    <Select.Option key={'family'} value={'family'}>家庭房</Select.Option>
                    <Select.Option key={'presidential'} value={'presidential'}>總統套房</Select.Option>
                    <Select.Option key={'quad'} value={'quad'}>四小床</Select.Option>
                    <Select.Option key={'twin'} value={'twin'}>雙床房</Select.Option>
                    <Select.Option key={'twin-double'} value={'twin-double'}>兩大床</Select.Option>
                </Select>
            </Form.Item>
        ) : (
            <div
                className="editable-cell-value-wrap"
                style={{ paddingRight: 24 }}
                onClick={toggleEdit}
            >
                {children.join("") === "" ? '-' : children}
            </div>
        );
    }

    return <td {...restProps}>{childNode}</td>;
};

export class RoomTypePage extends Component {
    state = {
        loading: false,
        details: [],
        selectHotel: null,
        selectCategory: null,
        // 快取過濾選項
        nameFilters: [],
        showAllRoomTypes: false, // 新增：控制是否顯示所有房型
    };
    componentDidMount() {
        this.refresh();
    }
    refresh = () => {
        this.setState({ loading: true });
        return PricingAPI.roomtypes.index({}, 1, 999).then((response) => {
            const result = response.data;
            const sortedData = result.data.sort((a, b) => a.rank - b.rank);
            this.setState({ details: sortedData, loading: false }, () => {
                // 更新過濾選項快取
                this.updateFilterOptions();
            });
        }).catch((error) => {
            console.error('Failed to refresh data:', error);
            this.setState({ loading: false });
        });
    }
    
    // 更新過濾選項快取
    updateFilterOptions = () => {
        const nameFilters = [...new Set(this.getDetails().map(item => item.name).filter(Boolean))].map(name => ({
            text: name,
            value: name,
        }));
        
        this.setState({ nameFilters });
    }
    handleSelectHotel = (value) => {
        this.setState({ selectHotel: value });
    }
    handleSelectCategory = (value) => {
        this.setState({ selectCategory: value });
    }
    getDetails = () => {
        let filteredDetails = this.state.details.filter((item) =>
            (this.state.selectHotel && item.hotel_id === this.state.selectHotel) &&
            (!this.state.selectCategory || item.roomcategory_id === this.state.selectCategory)
        );

        // 如果沒有勾選顯示全部，則隱藏超過30天沒有最近價格的房型
        if (!this.state.showAllRoomTypes) {
            filteredDetails = filteredDetails.filter((item) => {
                if (!item.last_updated) return false;
                return moment().diff(item.last_updated, 'days') <= 30;
            });
        }

        return filteredDetails;
    }
    handleShowAllRoomTypesChange = (e) => {
        this.setState({ showAllRoomTypes: e.target.checked });
    }
    handleSaveRow = (value) => {
        this.setState({ loading: true });
        const id = value.id;
        const updateData = { roomcategory_id: value.roomcategory_id };
        
        PricingAPI.pricetypes.update(id, updateData).then((response) => {
            return this.refresh();
        }).catch((error) => {
            console.error('Failed to save room type:', error);
            this.setState({ loading: false });
        });
    }
    render() {
        const columns = [
            {
                title: '編號',
                dataIndex: 'id',
                key: 'id',
            },
            // {
            //     title: '飯店',
            //     dataIndex: 'hotel_id',
            //     key: 'hotel_id',
            // },
            {
                title: '名稱',
                dataIndex: 'name',
                key: 'name',
                filters: this.state.nameFilters,
                onFilter: (value, record) => record.name === value,
                filterSearch: true,
            },
            {
                title: '房型類型',
                dataIndex: 'roomcategory_id',
                key: 'roomcategory_id',
                onCell: (record) => ({
                    record,
                    editable: true,
                    dataIndex: 'roomcategory_id',
                    title: '房型類型',
                    handleSave: this.handleSaveRow,
                }),
            },

            {
                title: '容納人數',
                dataIndex: 'numofppl',
                key: 'numofppl',
                align: 'right',
                sorter: (a, b) => a.numofppl - b.numofppl,
            },
            {
                title: '規格',
                dataIndex: 'spec',
                key: 'spec',
            },
            {
                title: '平均價',
                dataIndex: 'price_avg',
                key: 'price_avg',
                align: 'right',
                render: (value) => Math.round(value).toLocaleString(),
                sorter: (a, b) => a.price_avg - b.price_avg,
            },
            {
                title: '最高價',
                dataIndex: 'price_max',
                key: 'price_max',
                align: 'right',
                render: (value) => Math.round(value).toLocaleString(),
                sorter: (a, b) => a.price_max - b.price_max,
            },
            {
                title: '最低價',
                dataIndex: 'price_min',
                key: 'price_min',
                align: 'right',
                render: (value) => Math.round(value).toLocaleString(),
                sorter: (a, b) => a.price_min - b.price_min,
            },
            {
                title: '最新價格時間',
                dataIndex: 'last_updated',
                key: 'last_updated',
                render: (value, record) => {
                    if (moment().diff(value, 'days') > 7) {
                        return <span style={{ color: 'red ' }}>{moment(value).format("llll")}</span>
                    }
                    return moment(value).format("llll")
                },
                sorter: (a, b) => new Date(a.last_updated) - new Date(b.last_updated),
                defaultSortOrder: 'descend',
            },
            {
                title: '建立時間',
                dataIndex: 'created_at',
                key: 'created_at',
                render: (value) => value ? moment(value).format("llll") : '-'
            },
            {
                title: '更新時間',
                dataIndex: 'updated_at',
                key: 'updated_at',
                render: (value) => value ? moment(value).format("llll") : '-'
            },
        ];
        return (
            <div>
                <h1>房型</h1>
                <Form layout="inline" style={{ marginBottom: 10 }}>
                    <Form.Item label="飯店">
                        <HotelSelect value={this.state.selectedHotel} onChange={this.handleSelectHotel} />
                    </Form.Item>
                    <Form.Item label="類型">
                        <RoomCategorySelect value={this.state.selectedCategory} onChange={this.handleSelectCategory} />
                    </Form.Item>
                    <Form.Item>
                        <Checkbox
                            checked={this.state.showAllRoomTypes}
                            onChange={this.handleShowAllRoomTypesChange}
                        >
                            顯示全部房型（包含超過30天無價格更新）
                        </Checkbox>
                    </Form.Item>
                </Form>
                <Table components={{
                    body: {
                        row: EditableRow,
                        cell: EditableCell,
                    }
                }} columns={columns} dataSource={this.getDetails()} rowKey="id" loading={this.state.loading} scroll={{ x: 'max-content' }} size="small" pagination={false} />
            </div>
        )
    }
}

export default RoomTypePage